import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {EventType, PartEvent} from '../../../../models/PartEvent';
import Modal from '../../../../../../components/modal';
import {EventTypeStepComponent} from './Steps/EventTypeStep';
import {TargetStepComponent} from './Steps/TargetStep';
import {DataFieldStepComponent} from './Steps/DataFieldStep';
import {InstructionStepComponent} from './Steps/InstructionStep';
import {AnchorStepComponent} from './Steps/AnchorStep';
import {ExpandedPart} from '../../../../business/expanded_parts/ExpandedPartsProvider';
import {MainVisualisationStepComponent} from './Steps/MainVisualisationStep';

interface OwnProps {
    expandedPart: ExpandedPart;
    onAdd: (partEvent: PartEvent) => void;
    onCancel: () => void;
}

enum WizardSteps {
    //Step 1
    EventType,

    //Step 2
    Target,
    DataField,
    Instruction,
    MainVisualisation,

    //Step 3
    Anchor,
}

interface EventTypeStep {
    step: WizardSteps.EventType;
}

interface TargetPartStep {
    step: WizardSteps.Target;
}

interface DataFieldPartStep {
    step: WizardSteps.DataField;
}

interface InstructionStep {
    step: WizardSteps.Instruction;
}

interface MainVisualisationStep {
    step: WizardSteps.MainVisualisation;
}

interface AnchorStep {
    step: WizardSteps.Anchor;
    event: Omit<PartEvent, 'anchor'>;
}

type Step = EventTypeStep | TargetPartStep | DataFieldPartStep | InstructionStep | MainVisualisationStep | AnchorStep;

export const AddEventWizard: FunctionComponent<OwnProps> = observer(({expandedPart, onAdd, onCancel}) => {
    const [step, setStep] = useState<Step>({
        step: WizardSteps.EventType,
    });

    function onSelectEventTypeToStep(eventType: EventType): Step {
        switch (eventType) {
            case EventType.Target:
                return {
                    step: WizardSteps.Target,
                };
            case EventType.DataField0:
            case EventType.DataField1:
            case EventType.DataField2:
            case EventType.DataField3:
                return {
                    step: WizardSteps.DataField,
                };
            case EventType.Instruction:
                return {
                    step: WizardSteps.Instruction,
                };
            case EventType.MainVisualisation:
                return {
                    step: WizardSteps.MainVisualisation,
                };
        }
    }

    function getContent(): JSX.Element {
        switch (step.step) {
            //Step 1
            case WizardSteps.EventType:
                return (
                    <EventTypeStepComponent
                        onSelect={(eventType) => setStep(onSelectEventTypeToStep(eventType))}
                        onCancel={onCancel}
                    />
                );

            //Step 2
            case WizardSteps.Target:
                return (
                    <TargetStepComponent
                        onSubmit={(partEvent) => setStep({step: WizardSteps.Anchor, event: partEvent})}
                        onCancel={onCancel}
                    />
                );
            case WizardSteps.DataField:
                return (
                    <DataFieldStepComponent
                        onSubmit={(partEvent) => setStep({step: WizardSteps.Anchor, event: partEvent})}
                        onCancel={onCancel}
                    />
                );
            case WizardSteps.Instruction:
                return (
                    <InstructionStepComponent
                        onSubmit={(partEvent) => setStep({step: WizardSteps.Anchor, event: partEvent})}
                        onCancel={onCancel}
                    />
                );
            case WizardSteps.MainVisualisation:
                return (
                    <MainVisualisationStepComponent
                        onSubmit={(partEvent) => setStep({step: WizardSteps.Anchor, event: partEvent})}
                        onCancel={onCancel}
                    />
                );

            //Step 3
            case WizardSteps.Anchor:
                return (
                    <AnchorStepComponent
                        expandedPart={expandedPart}
                        onSubmit={(anchor) =>
                            onAdd({
                                __uuid: step.event.__uuid,
                                key: step.event.key,
                                value: step.event.value,
                                //We know the event type to be of either one EventTypes.. not sure why TypeScript is complaining
                                type: step.event.type as any,
                                anchor: anchor,
                            })
                        }
                        onCancel={onCancel}
                    />
                );
        }
    }

    return (
        <Modal visible={true} onHide={onCancel}>
            {getContent()}
        </Modal>
    );
});
